import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faUpload, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/apiService';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import './styles/CustomizedVariable.css';

/**
 * Upload an Excel workbook of flat variables straight into table_single.
 * Column A = respondent id (matched against an existing SPSS variable);
 * every other column header = a variable name to add (new column + vtm
 * row) or replace (existing column, matched respondents only). Backend:
 * workRoutes.py's /upload-excel-variables.
 */
const ExcelVariableUploadModal = ({ isOpen, onClose, projectId, user }) => {
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [file, setFile] = useState(null);
  const [idVariable, setIdVariable] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const reset = () => {
    setFile(null);
    setIdVariable('');
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleUpload = async () => {
    if (!file) { toast.warning('Please choose an Excel workbook'); return; }
    if (!idVariable.trim()) { toast.warning('Please enter the ID variable name'); return; }

    const confirmed = await confirm({
      title: 'Upload Excel Variables',
      message: `This will match "${idVariable.trim()}" against each row's id and write every other column into table_single — replacing data for variables that already exist, and adding new ones. Proceed?`,
      confirmText: 'Upload',
    });
    if (!confirmed) return;

    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('idVariable', idVariable.trim());
      fd.append('projectId', projectId);
      fd.append('user', user || '');

      const resp = await fetch(`${apiService.baseURL}/upload-excel-variables`, {
        method: 'POST',
        body: fd,
      });
      const data = await resp.json();
      if (!data?.success) throw new Error(data?.error || 'Upload failed');
      setResult(data);
      toast.success(data.message || 'Excel variables uploaded');
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--above-settings" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>

        <div className="modal-header">
          <h3>Upload Excel Data</h3>
          <button onClick={handleClose} className="modal-close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="variable-name-input">
          <label>Workbook:</label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>

        <div className="variable-name-input">
          <label>ID Variable Name:</label>
          <input
            type="text"
            value={idVariable}
            onChange={(e) => setIdVariable(e.target.value)}
            placeholder="e.g. record / respid — the SPSS variable matching the first column"
          />
        </div>

        <div style={{
          border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px',
          fontSize: 12.5, lineHeight: 1.5, marginTop: 10, background: '#f9fafb', color: '#374151',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>How to format your file</div>
          <div>Column A = respondent id, matched against the SPSS variable named above. Every
            other column header = a variable name to write into <code>table_single</code>: if
            the variable already exists its data is replaced (for matched respondents only), if
            not it's added as a new column and registered in the variable map.</div>
          <div style={{ marginTop: 6 }}>Rows whose id doesn't match an existing respondent are
            skipped and reported after upload — they don't stop the rest of the file from
            importing. Variables already mapped to a grid table are skipped too (this uploader
            only writes flat variables).</div>
        </div>

        {result && (
          <div style={{ marginTop: 12, fontSize: 12.5 }}>
            <div><FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16a34a' }} />{' '}
              {result.matchedRespondents} respondent(s) matched</div>
            {result.added.length > 0 && (
              <div style={{ marginTop: 4 }}><strong>Added ({result.added.length}):</strong> {result.added.join(', ')}</div>
            )}
            {result.updated.length > 0 && (
              <div style={{ marginTop: 4 }}><strong>Replaced ({result.updated.length}):</strong> {result.updated.join(', ')}</div>
            )}
            {result.skipped.length > 0 && (
              <div style={{ marginTop: 4, color: '#b45309' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> Skipped: {result.skipped.map(s => `${s.name} (${s.reason})`).join('; ')}
              </div>
            )}
            {result.errors.length > 0 && (
              <div style={{ marginTop: 4, color: '#dc2626' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> Errors: {result.errors.join('; ')}
              </div>
            )}
            {result.unmatchedIdCount > 0 && (
              <div style={{ marginTop: 4, color: '#b45309' }}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {result.unmatchedIdCount} id(s) did not match any
                respondent: {result.unmatchedIds.slice(0, 10).join(', ')}{result.unmatchedIdCount > 10 ? '…' : ''}
              </div>
            )}
          </div>
        )}

        <div className="modal-footer">
          <div className="footer-buttons">
            <button className="add-row-btn" onClick={handleClose} disabled={uploading}>
              Close
            </button>
            <button className="save-btn" onClick={handleUpload} disabled={uploading}>
              {uploading
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Uploading…</>
                : <><FontAwesomeIcon icon={faUpload} /> Upload</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelVariableUploadModal;
